/**
 * Every user-visible string for the pool Settings Awards card, its
 * archive-versus-delete chooser, its archive sheet, its delete confirm and
 * its create/edit sheet. Verbatim from `.agents/views/29-custom-awards.html`.
 *
 * The counter form `Medals · N of 5 used` deliberately follows the frame
 * rather than `issue.md` R1's paraphrase (`N of 5 medals`): the authored
 * design wins over a prose paraphrase of it.
 */

export type typeAwardKind = "medal" | "trophy";

export function kindLabelPlural(kind: typeAwardKind): string {
  return kind === "medal" ? "Medals" : "Trophies";
}

export function kindLabelSingular(kind: typeAwardKind): string {
  return kind === "medal" ? "Medal" : "Trophy";
}

export function kindNoun(kind: typeAwardKind, count: number): string {
  const isPlural = count !== 1;
  if (kind === "medal") {
    return isPlural ? "medals" : "medal";
  }
  return isPlural ? "trophies" : "trophy";
}

export function memberWord(count: number): string {
  return count !== 1 ? "members" : "member";
}

export const AWARDS_CARD_ARCHIVED_LABEL = "Archived";
export const AWARDS_CHOOSER_CANCEL_LABEL = "Cancel";
export const AWARDS_ARCHIVE_ANNOUNCE_LABEL = "Announce the archive in chat";
export const AWARDS_ARCHIVE_CONFIRM_LABEL = "Archive";
export const AWARDS_DELETE_CONFIRM_LABEL = "Delete award";
export const AWARDS_ROW_DELETE_LABEL = "Delete";
export const AWARDS_ROW_RESTORE_LABEL = "Restore";
export const AWARDS_CHOOSER_ARCHIVE_LABEL = "Archive";
export const AWARDS_CHOOSER_DELETE_LABEL = "Delete everywhere";
export const AWARDS_FORM_NAME_LABEL = "Name";
export const AWARDS_FORM_COLOR_LABEL = "Color";
export const AWARDS_FORM_PREVIEW_LABEL = "Preview";
export const AWARDS_FORM_PREVIEW_PLACEHOLDER = "Your award chip appears here";
export const AWARDS_FORM_CANCEL_LABEL = "Cancel";
export const AWARDS_EDIT_TITLE = "Rename or recolor";
export const AWARDS_EDIT_SAVE_LABEL = "Save changes";
export const AWARDS_EDIT_ARCHIVED_PILL = "Archived";
export const AWARDS_EDIT_ARCHIVED_NOTE =
  "Not on the give-out list. Restore it from the Awards card.";
export const AWARDS_LOADING_LABEL = "Loading awards";
export const AWARDS_FAILED_MESSAGE = "We could not load this pool's awards.";
export const AWARDS_RETRY_LABEL = "Try again";

export function awardsCounterLabel(activeCount: number): string {
  return `· ${activeCount} of 5 used`;
}

export function awardsGrantLine(input: {
  grantCount: number;
  memberCount: number;
}): string {
  if (input.grantCount === 0) {
    return "Not given yet";
  }
  if (input.memberCount === 1) {
    return "Given to 1 member";
  }
  return `Given to ${input.memberCount} members`;
}

export function awardsArchivedGrantLine(input: {
  kind: typeAwardKind;
  grantCount: number;
  memberCount: number;
}): string {
  return `${kindLabelSingular(input.kind)} · ${awardsGrantLine(input)}`;
}

export function awardsCreateLabel(kind: typeAwardKind): string {
  return `Create a ${kindNoun(kind, 1)} type`;
}

export function awardsCapReasonLabel(kind: typeAwardKind): string {
  // The frame's pinned string uses the singular noun as a compound-noun
  // modifier ("medal types"), not the plural form kindNoun(kind, 2) would
  // give ("medals types"). See `.agents/views/29-custom-awards.html`.
  return `You've used all 5 ${kindNoun(kind, 1)} types.`;
}

export function awardsRestoreBlockedReason(kind: typeAwardKind): string {
  return `5 of 5 ${kindNoun(kind, 1)} types active. Archive or delete one to restore this.`;
}

export function awardsChooserTitle(name: string): string {
  return `What do you want to do with "${name}"?`;
}

export function awardsChooserArchiveDescription(input: {
  kind: typeAwardKind;
  grantCount: number;
}): string {
  return `Keeps the ${input.grantCount} ${kindNoun(input.kind, input.grantCount)} already given. Stops handing out new ones.`;
}

export function awardsChooserDeleteDescription(kind: typeAwardKind): string {
  return `Removes the type and every ${kindNoun(kind, 1)} it gave out, on every profile.`;
}

export function awardsDeleteConfirmTitle(name: string): string {
  return `Delete "${name}"?`;
}

export function awardsDeleteConfirmBody(input: {
  kind: typeAwardKind;
  grantCount: number;
  memberCount: number;
}): string {
  return `This removes ${input.grantCount} ${kindNoun(input.kind, input.grantCount)} from ${input.memberCount} ${memberWord(input.memberCount)}. This can't be undone.`;
}

export function awardsArchiveTitle(name: string): string {
  return `Archive "${name}"?`;
}

export function awardsArchiveBody(input: {
  kind: typeAwardKind;
  grantCount: number;
  memberCount: number;
}): string {
  const noun = kindNoun(input.kind, input.grantCount);
  const profileClause =
    input.memberCount === 1
      ? "that 1 member's profile"
      : `those ${input.memberCount} members' profiles`;
  return `The ${input.grantCount} ${noun} already given stay on ${profileClause}. This type won't show up to hand out anymore, and you can restore it later.`;
}

export function awardsCreateTitle(kind: typeAwardKind): string {
  return `Create a ${kindNoun(kind, 1)} type`;
}

export function awardsCreateSubmitLabel(kind: typeAwardKind): string {
  return `Create ${kindNoun(kind, 1)} type`;
}

export function awardsEditPropagationLine(grantCount: number): string {
  if (grantCount === 0) {
    return "Renaming this changes it everywhere.";
  }
  return `Renaming this changes it everywhere, including the ${grantCount} already given out.`;
}

export function awardsCharCounter(length: number): string {
  return `${length}/30`;
}
